import React from "react";
import { ArrowUpOutlined, ArrowDownOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { apiBase } from "../utils/apiBase";
import { get } from "lodash";
import styles from "./CommoditySpotPrices.module.scss";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(timezone);
dayjs.extend(utc);

/**
 * CommoditySpotPrices Component
 *
 * Displays commodity spot prices (Natural Gas, Crude Oil) with periodic polling.
 * Designed to be compact and displayed in the header next to the logo.
 *
 * Features:
 * - Periodic price updates via polling (every 60 seconds)
 * - Compact horizontal layout
 * - Color-coded price changes
 * - Automatic cleanup on unmount
 */
const CommoditySpotPrices = () => {
  const { data: pricesData } = useQuery({
    queryKey: ["commoditySpotPrices"],
    queryFn: () =>
      apiBase
        .get("/commodity-spot-prices")
        .then((res) => get(res, "data", null)),
    refetchInterval: 60_000,
    refetchIntervalInBackground: true,
  });

  const getChangeIcon = (change) => {
    if (!change || change === 0) return null;
    return change > 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />;
  };

  const getChangeClass = (change) => {
    if (!change || change === 0) return styles.neutral;
    return change > 0 ? styles.positive : styles.negative;
  };

  if (!pricesData) {
    return null;
  }

  // Extract natural gas and crude oil data
  const naturalGas = pricesData.natural_gas;
  const crudeOil = pricesData.crude_oil;

  const commodityData = crudeOil || naturalGas;

  const timestamp = commodityData?.timestamp
    ? dayjs.utc(commodityData.timestamp).local().format("h:mm A")
    : null;

  return (
    <div className={styles.container}>
      {crudeOil && (
        <a
          href="https://finance.yahoo.com/quote/CL=F/"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.commodity}
          style={{ textDecoration: "none", color: "inherit" }}
        >
          <span className={styles.symbol}>{crudeOil.symbol}</span>
          <span
            className={`${styles.price} ${getChangeClass(
              crudeOil.daily_change
            )}`}
          >
            ${crudeOil.current_price.toFixed(2)}
          </span>
          <span
            className={`${styles.change} ${getChangeClass(
              crudeOil.daily_change
            )}`}
          >
            {getChangeIcon(crudeOil.daily_change)}
            {Math.abs(crudeOil.percent_change).toFixed(2)}%
          </span>
        </a>
      )}

      {naturalGas && (
        <a
          href="https://finance.yahoo.com/quote/NG=F/"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.commodity}
          style={{ textDecoration: "none", color: "inherit" }}
        >
          <span className={styles.symbol}>{naturalGas.symbol}</span>
          <span
            className={`${styles.price} ${getChangeClass(
              naturalGas.daily_change
            )}`}
          >
            ${naturalGas.current_price.toFixed(4)}
          </span>
          <span
            className={`${styles.change} ${getChangeClass(
              naturalGas.daily_change
            )}`}
          >
            {getChangeIcon(naturalGas.daily_change)}
            {Math.abs(naturalGas.percent_change).toFixed(2)}%
          </span>
        </a>
      )}

      {timestamp && <div className={styles.timestamp}>{timestamp}</div>}
    </div>
  );
};

export default CommoditySpotPrices;
